/**
 * Routes (§12.3).
 *
 * Phase 0 ships the auth surface and the app shell. The feature routes — tunnels, inspector,
 * domains, traffic, logs, settings — are declared as placeholders so the sidebar's nav is
 * real (a dead link in the shell would fail the visual-regression comparison against the kit
 * screen, and would be a lie in the UI).
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/workspace'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { guest: true, title: 'Sign in' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/auth/RegisterPage.vue'),
    meta: { guest: true, title: 'Create your account' },
  },
  {
    path: '/2fa',
    name: 'two-factor',
    component: () => import('@/pages/auth/TwoFactorPage.vue'),
    meta: { guest: true, title: 'Two-factor authentication' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
    meta: { guest: true, title: 'Reset your password' },
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: () => import('@/pages/auth/ResetPasswordPage.vue'),
    meta: { guest: true, title: 'Choose a new password' },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/pages/auth/VerifyEmailPage.vue'),
    meta: { auth: true, title: 'Verify your email' },
  },
  {
    path: '/oauth/callback',
    name: 'oauth-callback',
    component: () => import('@/pages/auth/OAuthCallbackPage.vue'),
    meta: { title: 'Signing you in' },
  },

  {
    path: '/',
    component: () => import('@/components/layout/AppShell.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'overview',
        component: () => import('@/pages/app/OverviewPage.vue'),
        meta: { title: 'Overview' },
      },
      // Declared so the shell's navigation is real. Each lands with its phase.
      { path: 'tunnels', name: 'tunnels', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Tunnels', phase: 'Phase 1' } },
      { path: 'inspector', name: 'inspector', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Request Inspector', phase: 'Phase 2' } },
      { path: 'endpoints', name: 'endpoints', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Endpoints', phase: 'Phase 3' } },
      { path: 'domains', name: 'domains', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Domains & DNS', phase: 'Phase 3' } },
      { path: 'traffic', name: 'traffic', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Traffic & Analytics', phase: 'Phase 2' } },
      { path: 'logs', name: 'logs', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Logs', phase: 'Phase 2' } },
      { path: 'settings/access', name: 'access-policies', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Access Policies', phase: 'Phase 4' } },
      { path: 'settings/tokens', name: 'tokens', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'API Keys & CLI Tokens', phase: 'Phase 1' } },
      { path: 'settings/billing', name: 'billing', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Usage & Billing', phase: 'Phase 5' } },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/pages/app/SettingsPage.vue'),
        meta: { title: 'Settings' },
      },
    ],
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { title: 'Not found' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // One session probe per page load. Guards on every route would otherwise each fire their
  // own /v1/me on the first navigation.
  if (!auth.resolved) {
    await auth.fetchUser()

    if (auth.isAuthenticated) {
      const workspaces = useWorkspaceStore()
      workspaces.setWorkspaces(auth.user?.workspaces ?? [])
    }
  }

  if (to.meta.auth && !auth.isAuthenticated) {
    // Remember where they were headed so the post-login redirect is not always the overview.
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
  }

  if (to.meta.guest && auth.isAuthenticated) {
    return { name: 'overview' }
  }

  return true
})

router.afterEach((to) => {
  const title = to.meta.title as string | undefined
  document.title = title ? `${title} · nlocal` : 'nlocal'
})
